import { useTranslation } from "react-i18next";

export const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "2-digit",
  };

  const weekday = date.toLocaleDateString("en-US", {
    weekday: options.weekday,
  });
  const dayMonthYear = date.toLocaleDateString("en-US", {
    year: options.year,
    month: options.month,
    day: options.day,
  });

  return `${weekday}, ${dayMonthYear}`;
};

export const useRelativeTime = () => {
  const { t } = useTranslation("chatWithExperts");

  return (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    const units: { [key: string]: number } = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60,
    };

    for (const unit in units) {
      const unitSeconds = units[unit];
      if (diffInSeconds >= unitSeconds) {
        const value = Math.floor(diffInSeconds / unitSeconds);
        const unitTranslation = t(
          `chat.units.${unit}.${value > 1 ? "pl" : "sg"}`,
        );
        const ago = t("chat.ago");
        return `${value} ${unitTranslation} ${ago}`;
      }
    }

    return t("chat.justNow");
  };
};

export const formatTime = (seconds: number) => {
  if (seconds <= 0) return "00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return `${h > 0 ? `${h}:` : ""}${m.toString().padStart(2, "0")}:${s
    .toString()
    .padStart(2, "0")}`;
};

// Server timestamps are naive wall-clock times in Europe/Belgrade (TZ of the
// backend containers), which has the same offset as Germany all year. They are
// shown as they come, so every viewer sees German time whatever their browser's
// time zone.
const SERVER_TIMESTAMP = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/;

export const formatMessageTime = (dateString: string) => {
  const parts = SERVER_TIMESTAMP.exec(dateString ?? "");
  if (!parts) return dateString ?? "";

  const [, , , , hour, minute] = parts;
  return `${hour}:${minute}`;
};

export const formatMessageDateTime = (dateString: string) => {
  const parts = SERVER_TIMESTAMP.exec(dateString ?? "");
  if (!parts) return dateString ?? "";

  const [, year, month, day, hour, minute] = parts;
  return `${day}.${month}.${year} ${hour}:${minute}`;
};

export const parseDateUTC = (dateStr: string | undefined | null): number => {
  if (!dateStr) return 0;
  const cleaned = dateStr.replace(/\.(\d{3})\d+/, ".$1");
  const timestamp = new Date(cleaned).getTime();

  return isNaN(timestamp) ? 0 : timestamp;
};

export const formatLoginTime = (dateString: string | undefined): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return date.toUTCString();
};

export const formatTimeAgo = (timeStart: number): string => {
  const now = Date.now() / 1000;
  const diff = Math.max(0, Math.floor(now - timeStart));

  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s ago`;
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  return `${hours}h ${minutes}m ago`;
};
